import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  FormGroup,
  Typography,
} from '@mui/material'
import { SummaryTable } from 'components/SummaryTable'
import { OpenJsonButton } from 'components/utility/OpenJsonButton'
import { downloadJson } from 'shared/utils/fileExport'
import {
  getDiscoverySummaryRows,
  getStatusTableRows,
  getUploadSummaryRows,
  getUrlResultRows,
} from './formatUtils'
import { useUploadStage } from './hooks'
import { StageProps } from './types'

export function UploadStage({ treeId, versionId }: StageProps) {
  const {
    uploadStatus,
    uploadResult,
    includeImages,
    includeDocuments,
    includeAudioVideo,
    isRunning,
    setIncludeImages,
    setIncludeDocuments,
    setIncludeAudioVideo,
    handleStartStage,
    canStart,
  } = useUploadStage(treeId, versionId)

  const discoveryRows = getDiscoverySummaryRows(uploadResult?.discovery)
  const uploadSummaryRows = getUploadSummaryRows(uploadResult?.summary)
  const urlResultRows = getUrlResultRows(
    uploadResult?.node_upload_results,
    uploadResult?.leaf_upload_results,
  )

  return (
    <Box border={1} borderColor="grey.300" borderRadius={1} p={2} mb={2}>
      <Typography variant="h6" gutterBottom sx={{ fontStyle: 'italic' }}>
        Upload
      </Typography>

      <Box display="flex" alignItems="center" gap={2} mb={2}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleStartStage}
          disabled={!canStart}
          size="small"
        >
          Start
        </Button>
      </Box>

      <Box sx={{ mb: 2 }}>
        <FormGroup row>
          <FormControlLabel
            control={
              <Checkbox
                checked={includeImages}
                onChange={(event) => setIncludeImages(event.target.checked)}
                size="small"
                disabled={isRunning}
              />
            }
            label="Images"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={includeDocuments}
                onChange={(event) => setIncludeDocuments(event.target.checked)}
                size="small"
                disabled={isRunning}
              />
            }
            label="Documents"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={includeAudioVideo}
                onChange={(event) => setIncludeAudioVideo(event.target.checked)}
                size="small"
                disabled={isRunning}
              />
            }
            label="Audio/Video"
          />
        </FormGroup>
      </Box>

      {uploadStatus && (
        <Box sx={{ mb: 2 }}>
          {!uploadStatus.completed && (
            <Box sx={{ mb: 1 }}>
              <CircularProgress size={20} color="primary" />
            </Box>
          )}
          <SummaryTable
            rows={getStatusTableRows(uploadStatus)}
            firstColumnSx={{ fontStyle: 'italic' }}
          />
        </Box>
      )}

      {uploadResult && (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <OpenJsonButton
              data={uploadResult}
              disabled={!uploadResult}
              buttonText="See JSON results"
            />
            <Button
              variant="text"
              size="small"
              onClick={() => {
                downloadJson(
                  `asset_upload_results_${treeId}_${versionId}.json`,
                  uploadResult,
                )
              }}
              sx={{ textTransform: 'none', fontSize: '0.75rem' }}
            >
              Download JSON
            </Button>
          </Box>

          <SummaryTable
            header="Discovery Summary"
            rows={discoveryRows}
            firstColumnSx={{ fontStyle: 'italic' }}
          />
          <Box sx={{ mt: 1 }}>
            <SummaryTable
              header="Upload Summary"
              rows={uploadSummaryRows}
              firstColumnSx={{ fontStyle: 'italic' }}
            />
          </Box>

          <Box sx={{ mt: 1 }}>
            <SummaryTable
              header="URL Results"
              headers={[
                'Type',
                'ID',
                'Field',
                'Status',
                'Previous URL',
                'New URL',
              ]}
              rows={urlResultRows}
              firstColumnSx={{ fontStyle: 'italic' }}
            />
          </Box>
        </>
      )}
    </Box>
  )
}
